import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { type SdmModuleSpecsFragment } from '../../../../../../api/fragments';
import {
    CreateExternalBankDocument,
    type CreateExternalBankMutation,
    type CreateExternalBankMutationVariables,
} from '../../../../../../api/mutations/createExternalBank';
import {
    type BankIntegrationProvider,
    type BankIntegrationSettings,
    type ExternalBankSettings,
} from '../../../../../../api/types';
import Form from '../../../../../../components/fields/Form';
import useHandleError from '../../../../../../utilities/useHandleError';
import { getBankIntegrationSettings } from '../../../../AddBankPage';
import { defaultIntegrationSettings } from '../../../../BankDetailsPage/BankForm/CreateBankFrom';
import BankFields from './BankFields';
import useBankIntegrationValidator from './useBankIntegrationValidator';

export type CreateBankIntegrationValues = ExternalBankSettings & {
    integrationProvider: BankIntegrationProvider;
    integrationSettings: BankIntegrationSettings;
};

export const defaultValues: CreateBankIntegrationValues = {
    displayName: '',
    identifier: '',
    dealerCode: '',
    integrationProvider: null,
    integrationSettings: defaultIntegrationSettings,
};

export type CreateBankIntegrationFormProps = {
    module: SdmModuleSpecsFragment;
    onClose: () => void;
};

const CreateBankIntegrationForm = ({ module, onClose }: CreateBankIntegrationFormProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const apolloClient = useApolloClient();
    const validate = useBankIntegrationValidator();

    const onSubmit = useHandleError<CreateBankIntegrationValues>(
        async values => {
            const { integrationProvider, integrationSettings, ...settings } = values;

            // submitting message
            message.loading({
                content: t('sdmModuleDetails:messages.creatingBankIntegration'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<CreateExternalBankMutation, CreateExternalBankMutationVariables>({
                    mutation: CreateExternalBankDocument,
                    variables: {
                        moduleId: module.id,
                        settings,
                        integrationSettings: getBankIntegrationSettings({ integrationProvider, integrationSettings }),
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('sdmModuleDetails:messages.createSuccess'),
                key: 'primary',
            });

            // close the drawer
            onClose();
        },
        [apolloClient, module.id, onClose, t]
    );

    return (
        <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit, values }) => (
                <Form id="bankIntegrationForm" name="bankIntegrationForm" onSubmitCapture={handleSubmit}>
                    <BankFields provider={values.integrationProvider} />
                </Form>
            )}
        </Formik>
    );
};

export default CreateBankIntegrationForm;
